// src/pages/SuperAdminPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FormHelperText,
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  PersonOutline as PersonOutlineIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { Hotel, GuestStatistics } from '../types/chat';
import { useTranslation } from 'react-i18next';

type DialogSeverity = 'info' | 'success' | 'warning' | 'error';

interface InfoDialogState {
  open: boolean;
  title: string;
  message: string;
  severity: DialogSeverity;
}

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
}

const SuperAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [statistics, setStatistics] = useState<GuestStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState<{ email: string; password: string } | null>(null);
  const [infoDialog, setInfoDialog] = useState<InfoDialogState>({
    open: false,
    title: '',
    message: '',
    severity: 'info',
  });
  
  const [confirmDialogState, setConfirmDialogState] = useState<ConfirmDialogState>({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Onayla',
    cancelLabel: 'İptal',
  });
  
  const confirmDialogResolverRef = useRef<((value: boolean) => void) | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    isActive: true,
  });

  const emptyEditForm = {
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    whatsappLineId: '',
    dashboardCompanyId: '',
    dashboardBranchId: '',
    dashboardChatbotName: '',
    isActive: true,
  };
  const [editDialog, setEditDialog] = useState(false);
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null);
  const [editFormData, setEditFormData] = useState(emptyEditForm);
  const [migrationStatus, setMigrationStatus] = useState<{ executed: string[]; pending: string[] } | null>(null);
  const [runningMigrations, setRunningMigrations] = useState(false);

  const closeInfoDialog = () => {
    setInfoDialog((prev) => ({ ...prev, open: false }));
  };
  
  const showInfoDialog = (message: string, options?: { title?: string; severity?: DialogSeverity }) => {
    setInfoDialog({
      open: true,
      title: options?.title || 'Bilgilendirme',
      message,
      severity: options?.severity || 'info',
    });
  };
  
  const requestConfirmation = (options: { title?: string; message: string; confirmLabel?: string; cancelLabel?: string }) => {
    return new Promise<boolean>((resolve) => {
      confirmDialogResolverRef.current = resolve;
      setConfirmDialogState({
        open: true,
        title: options.title || 'Onay gerekli',
        message: options.message,
        confirmLabel: options.confirmLabel || 'Onayla',
        cancelLabel: options.cancelLabel || 'İptal',
      });
    });
  };
  
  const handleConfirmDialogClose = (result: boolean) => {
    setConfirmDialogState((prev) => ({ ...prev, open: false }));
    confirmDialogResolverRef.current?.(result);
    confirmDialogResolverRef.current = null;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch from both sources in parallel
      const [localResponse, pmsResponse, statsResponse, migrationsResponse] = await Promise.allSettled([
        api.get('/hotels'),
        api.get('/gsc-api/hotels'),
        api.get('/users/statistics'),
        api.get('/system/migrations'),
      ]);

      const localHotels = localResponse.status === 'fulfilled' ? localResponse.value.data : [];
      const pmsHotels = pmsResponse.status === 'fulfilled' ? pmsResponse.value.data : [];

      const combined = [...localHotels, ...pmsHotels];
      setHotels(combined);
      
      if (statsResponse.status === 'fulfilled') {
        setStatistics(statsResponse.value.data);
      }

      if (migrationsResponse.status === 'fulfilled') {
        setMigrationStatus(migrationsResponse.value.data);
      }
      
      setError(null);
    } catch (err: any) {
      // Handle validation errors (array) or single error message
      let errorMessage = t('superAdmin.failedToLoadData');
      
      if (err.response?.data?.message) {
        // If message is an array (validation errors), join them
        if (Array.isArray(err.response.data.message)) {
          errorMessage = err.response.data.message.join(', ');
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      showInfoDialog(errorMessage, {
        severity: 'error',
        title: t('superAdmin.error') || 'Hata',
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateHotel = async () => {
    try {
      const response = await api.post('/hotels', formData);
      setOpenDialog(false);
      
      // Show admin credentials if they were created
      if (response.data.adminUser && response.data.adminUser.adminEmail && response.data.adminUser.adminPassword) {
        setAdminCredentials({
          email: response.data.adminUser.adminEmail,
          password: response.data.adminUser.adminPassword,
        });
        setCredentialsDialog(true);
      }
      
      setFormData({
        name: '',
        code: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        isActive: true,
      });
      
      fetchData();
    } catch (err: any) {
      // Handle validation errors (array) or single error message
      let errorMessage = t('superAdmin.failedToCreateHotel');
      
      console.log('Error response:', err.response?.data); // Debug log
      
      if (err.response?.data?.message) {
        // If message is an array (validation errors), join them
        if (Array.isArray(err.response.data.message)) {
          errorMessage = err.response.data.message.join(', ');
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      showInfoDialog(errorMessage, {
        severity: 'error',
        title: t('superAdmin.error') || 'Hata',
      });
      console.error('Error creating hotel:', err);
    }
  };

  const handleOpenEditDialog = (hotel: Hotel) => {
    setEditingHotel(hotel);
    setEditFormData({
      name: hotel.name || '',
      code: hotel.code || '',
      address: hotel.address || '',
      city: hotel.city || '',
      state: hotel.state || '',
      zipCode: hotel.zipCode || '',
      country: hotel.country || '',
      phone: hotel.phone || '',
      email: hotel.email || '',
      website: hotel.website || '',
      description: hotel.description || '',
      whatsappLineId: hotel.whatsappLineId || '',
      dashboardCompanyId: hotel.dashboardCompanyId || '',
      dashboardBranchId: hotel.dashboardBranchId || '',
      dashboardChatbotName: hotel.dashboardChatbotName || '',
      isActive: hotel.isActive,
    });
    setEditDialog(true);
  };

  const handleUpdateHotel = async () => {
    if (!editingHotel) return;
    try {
      // Boş string'leri gönderme (IsEmail vb. validasyonlara takılır);
      // whatsapp / dashboard scope boşsa null gönder ki eşleme temizlenebilsin.
      const {
        whatsappLineId,
        dashboardCompanyId,
        dashboardBranchId,
        dashboardChatbotName,
        isActive,
        ...rest
      } = editFormData;
      const payload: Record<string, any> = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => typeof v !== 'string' || v.trim() !== '')
      );
      payload.whatsappLineId = String(whatsappLineId ?? '').trim() || null;
      payload.dashboardCompanyId = String(dashboardCompanyId ?? '').trim() || null;
      payload.dashboardBranchId = String(dashboardBranchId ?? '').trim() || null;
      // Non-empty name → Gemini key + prompt/KB/MCP from that Dashboard chatbot.
      payload.dashboardChatbotName = String(dashboardChatbotName ?? '').trim() || null;
      payload.isActive = isActive;

      const { data: updated } = await api.patch(`/hotels/${editingHotel.id}`, payload);
      // Backend eskiyse whitelist bu alanları yutar; sessizce kaybolmasın.
      if (
        payload.dashboardCompanyId &&
        (!updated || updated.dashboardCompanyId !== payload.dashboardCompanyId)
      ) {
        throw new Error(
          t('superAdmin.dashboardScopeSaveFailed') ||
            'Dashboard Company/Branch ID kaydedilmedi. Backend’i güncel kodla yeniden başlatın.'
        );
      }
      setEditDialog(false);
      setEditingHotel(null);
      setEditFormData(emptyEditForm);

      showInfoDialog(
        t('superAdmin.hotelUpdatedSuccessfully') || 'Otel başarıyla güncellendi!',
        {
          severity: 'success',
          title: t('superAdmin.success') || 'Başarılı',
        }
      );

      fetchData();
    } catch (err: any) {
      let errorMessage = t('superAdmin.failedToUpdateHotel') || 'Otel güncellenemedi';

      if (err.response?.data?.message) {
        if (Array.isArray(err.response.data.message)) {
          errorMessage = err.response.data.message.join(', ');
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      showInfoDialog(errorMessage, {
        severity: 'error',
        title: t('superAdmin.error') || 'Hata',
      });
      console.error('Error updating hotel:', err);
    }
  };

  const handleRunMigrations = async () => {
    const confirmed = await requestConfirmation({
      title: t('superAdmin.runMigrations') || "Migration'ları Çalıştır",
      message: t('superAdmin.runMigrationsConfirmation') || 'Bekleyen veritabanı migrationları çalıştırılacak. Devam edilsin mi?',
      confirmLabel: t('superAdmin.runMigrations') || 'Çalıştır',
      cancelLabel: t('superAdmin.cancel') || 'İptal',
    });

    if (!confirmed) {
      return;
    }

    try {
      setRunningMigrations(true);
      const response = await api.post('/system/migrations/run');
      const ran: string[] = response.data?.ran || [];

      showInfoDialog(
        ran.length > 0
          ? `${t('superAdmin.migrationsRanSuccessfully') || "Migration'lar başarıyla çalıştırıldı"}:\n\n${ran.join('\n')}`
          : t('superAdmin.noPendingMigrations') || 'Bekleyen migration yok, veritabanı güncel.',
        {
          severity: 'success',
          title: t('superAdmin.success') || 'Başarılı',
        }
      );

      fetchData();
    } catch (err: any) {
      let errorMessage = t('superAdmin.failedToRunMigrations') || "Migration'lar çalıştırılamadı";

      if (err.response?.data?.message) {
        if (Array.isArray(err.response.data.message)) {
          errorMessage = err.response.data.message.join(', ');
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      showInfoDialog(errorMessage, {
        severity: 'error',
        title: t('superAdmin.error') || 'Hata',
      });
      console.error('Error running migrations:', err);
    } finally {
      setRunningMigrations(false);
    }
  };

  const handleDeleteHotel = async (id: string) => {
    const confirmed = await requestConfirmation({
      title: t('superAdmin.deleteHotel') || 'Oteli Sil',
      message: t('superAdmin.deleteHotelConfirmation') || 'Bu oteli silmek istediğinizden emin misiniz?',
      confirmLabel: t('superAdmin.delete') || 'Sil',
      cancelLabel: t('superAdmin.cancel') || 'İptal',
    });
    
    if (!confirmed) {
      return;
    }
    
    try {
      await api.delete(`/hotels/${id}`);
      
      showInfoDialog(
        t('superAdmin.hotelDeletedSuccessfully') || 'Otel başarıyla silindi!',
        {
          severity: 'success',
          title: t('superAdmin.success') || 'Başarılı',
        }
      );
      
      fetchData();
    } catch (err: any) {
      // Handle validation errors (array) or single error message
      let errorMessage = t('superAdmin.failedToDeleteHotel');
      
      if (err.response?.data?.message) {
        // If message is an array (validation errors), join them
        if (Array.isArray(err.response.data.message)) {
          errorMessage = err.response.data.message.join(', ');
        } else {
          errorMessage = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      showInfoDialog(errorMessage, {
        severity: 'error',
        title: t('superAdmin.error') || 'Hata',
      });
      console.error('Error deleting hotel:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: isDark ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.8)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(102, 126, 234, 0.1)',
        '& fieldset': {
          borderColor: isDark ? '#3b82f6' : '#667eea',
          borderWidth: 2,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.6)' : '0 6px 20px rgba(102, 126, 234, 0.15)',
        '& fieldset': {
          borderColor: isDark ? '#3b82f6' : '#667eea',
          borderWidth: 2.5,
        },
      },
      '& fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: 1.5,
        transition: 'all 0.3s ease',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: isDark ? theme.palette.text.secondary : '#6b7280',
      '&.Mui-focused': {
        color: isDark ? '#3b82f6' : '#667eea',
        fontWeight: 700,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 4 }, 
      width: '100%', 
      maxWidth: 1400, 
      mx: 'auto', 
      minHeight: '100vh', 
      overflow: 'auto',
      bgcolor: theme.palette.background.default,
    }}>
      <Box>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: isDark ? theme.palette.primary.light : '#667eea',
            }}
          >
            {t('superAdmin.title')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#5568d3',
              },
            }}
          >
            {t('superAdmin.createHotel')}
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2.5,
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(239, 68, 68, 0.15)',
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3,
                borderRadius: 3,
                border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon sx={{ fontSize: 40, mr: 2, color: '#667eea' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {hotels.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    {t('superAdmin.totalHotels')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3,
                borderRadius: 3,
                border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ fontSize: 40, mr: 2, color: '#667eea' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {statistics?.totalGuests || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    {t('superAdmin.totalGuests')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3,
                borderRadius: 3,
                border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonOutlineIcon sx={{ fontSize: 40, mr: 2, color: '#667eea' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {statistics?.activeGuests || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    {t('superAdmin.activeGuests')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Stack>

        {/* Guest Statistics by Hotel */}
        {statistics && (
          <Paper 
            elevation={2}
            sx={{ 
              mb: 4, 
              p: 3,
              borderRadius: 3,
              border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                color: isDark ? theme.palette.primary.light : '#667eea',
              }}
            >
              {t('superAdmin.guestStatisticsByHotel')}
            </Typography>
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('superAdmin.hotel')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('superAdmin.totalGuestsLabel')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('superAdmin.active')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('superAdmin.inactive')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.guestsByHotel && statistics.guestsByHotel.length > 0 ? (
                  statistics.guestsByHotel.map((hotel) => (
                    <TableRow key={hotel.hotelId}>
                      <TableCell>{hotel.hotelName}</TableCell>
                      <TableCell align="center">{hotel.totalGuests}</TableCell>
                      <TableCell align="center">
                        <Chip label={hotel.activeGuests} color="success" size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={hotel.inactiveGuests} color="default" size="small" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">{t('superAdmin.noHotelsFound')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

        {/* Hotels List */}
        <Paper 
          elevation={2}
          sx={{ 
            p: 3,
            borderRadius: 3,
            border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              color: isDark ? theme.palette.primary.light : '#667eea',
            }}
          >
            {t('superAdmin.hotelsManagement')}
          </Typography>
        <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('superAdmin.name')}</strong></TableCell>
                <TableCell><strong>{t('superAdmin.code')}</strong></TableCell>
                <TableCell><strong>{t('superAdmin.location')}</strong></TableCell>
                <TableCell><strong>{t('superAdmin.contact')}</strong></TableCell>
                <TableCell><strong>{t('superAdmin.whatsappLine') || 'WhatsApp Hattı'}</strong></TableCell>
                <TableCell><strong>{t('superAdmin.dashboardCompanyId')}</strong></TableCell>
                <TableCell><strong>{t('superAdmin.dashboardBranchId')}</strong></TableCell>
                <TableCell align="center"><strong>{t('superAdmin.status')}</strong></TableCell>
                <TableCell align="center"><strong>{t('superAdmin.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hotels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">{t('superAdmin.noHotelsCreateFirst')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                hotels.map((hotel) => (
                  <TableRow key={hotel.id}>
                    <TableCell>{hotel.name}</TableCell>
                    <TableCell>{hotel.code}</TableCell>
                    <TableCell>
                      {hotel.city && hotel.country ? `${hotel.city}, ${hotel.country}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {hotel.email || hotel.phone || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {hotel.whatsappLineId ? (
                        <Chip label={hotel.whatsappLineId} size="small" variant="outlined" color="success" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {hotel.dashboardCompanyId ? (
                        <Chip label={hotel.dashboardCompanyId} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {hotel.dashboardBranchId ? (
                        <Chip label={hotel.dashboardBranchId} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {hotel.isActive ? (
                        <Chip icon={<CheckCircleIcon />} label={t('superAdmin.active')} color="success" size="small" />
                      ) : (
                        <Chip icon={<CancelIcon />} label={t('superAdmin.inactive')} color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEditDialog(hotel)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteHotel(hotel.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

        {/* Database Migrations */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mt: 3,
            borderRadius: 3,
            border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: isDark ? theme.palette.primary.light : '#667eea',
            }}
          >
            {t('superAdmin.migrations') || 'Veritabanı Migrationları'}
          </Typography>
          {migrationStatus ? (
            <>
              {migrationStatus.pending.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {(t('superAdmin.pendingMigrations') || 'Bekleyen migrationlar')}:
                  <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
                    {migrationStatus.pending.map((name) => (
                      <li key={name}>
                        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {name}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t('superAdmin.noPendingMigrations') || 'Bekleyen migration yok, veritabanı güncel.'}
                </Alert>
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  disabled={runningMigrations || migrationStatus.pending.length === 0}
                  onClick={handleRunMigrations}
                  startIcon={runningMigrations ? <CircularProgress size={18} color="inherit" /> : undefined}
                  sx={{
                    backgroundColor: '#667eea',
                    '&:hover': {
                      backgroundColor: '#5568d3',
                    },
                  }}
                >
                  {t('superAdmin.runMigrations') || "Migration'ları Çalıştır"}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {(t('superAdmin.executedMigrations') || 'Çalıştırılmış')}: {migrationStatus.executed.length}
                </Typography>
              </Stack>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('superAdmin.migrationStatusUnavailable') || 'Migration durumu alınamadı.'}
            </Typography>
          )}
        </Paper>

        {/* Create Hotel Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 700,
            color: '#667eea',
          }}>
            {t('superAdmin.createNewHotel')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('superAdmin.hotelName')}
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.hotelCode')}
                  fullWidth
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  sx={textFieldSx}
                />
              </Stack>
              <TextField
                label={t('superAdmin.address')}
                fullWidth
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                sx={textFieldSx}
              />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('superAdmin.city')}
                fullWidth
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                sx={textFieldSx}
              />
              <TextField
                label={t('superAdmin.state')}
                fullWidth
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                sx={textFieldSx}
              />
              <TextField
                label={t('superAdmin.zipCode')}
                fullWidth
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                sx={textFieldSx}
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('superAdmin.country')}
                fullWidth
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                sx={textFieldSx}
              />
              <TextField
                label={t('superAdmin.phone')}
                fullWidth
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                sx={textFieldSx}
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('superAdmin.email')}
                type="email"
                fullWidth
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={textFieldSx}
              />
              <TextField
                label={t('superAdmin.website')}
                fullWidth
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                sx={textFieldSx}
              />
            </Stack>
            <TextField
              label={t('superAdmin.description')}
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={textFieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
          >
            {t('login.cancel')}
          </Button>
          <Button 
            onClick={handleCreateHotel} 
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#5568d3',
              },
            }}
          >
            {t('superAdmin.createHotel')}
          </Button>
        </DialogActions>
      </Dialog>

        {/* Edit Hotel Dialog */}
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{
            fontWeight: 700,
            color: '#667eea',
          }}>
            {t('superAdmin.editHotel') || 'Oteli Düzenle'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('superAdmin.hotelName')}
                  fullWidth
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.hotelCode')}
                  fullWidth
                  required
                  value={editFormData.code}
                  onChange={(e) => setEditFormData({ ...editFormData, code: e.target.value })}
                  sx={textFieldSx}
                />
              </Stack>
              <TextField
                label={t('superAdmin.whatsappLine') || 'WhatsApp Hattı'}
                fullWidth
                value={editFormData.whatsappLineId}
                onChange={(e) => setEditFormData({ ...editFormData, whatsappLineId: e.target.value })}
                placeholder="908502600457"
                helperText={t('superAdmin.whatsappLineHelp') || 'Bu hattan gelen WhatsApp mesajları bu otele task açar. Boş bırakılırsa hat eşlemesi kaldırılır.'}
                sx={textFieldSx}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('superAdmin.dashboardCompanyId')}
                  fullWidth
                  value={editFormData.dashboardCompanyId}
                  onChange={(e) => setEditFormData({ ...editFormData, dashboardCompanyId: e.target.value })}
                  placeholder="48"
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.dashboardBranchId')}
                  fullWidth
                  value={editFormData.dashboardBranchId}
                  onChange={(e) => setEditFormData({ ...editFormData, dashboardBranchId: e.target.value })}
                  placeholder="72"
                  sx={textFieldSx}
                />
              </Stack>
              <FormHelperText sx={{ mt: -1, ml: 1.5 }}>
                {t('superAdmin.dashboardScopeHelp')}
              </FormHelperText>
              <TextField
                label={t('superAdmin.dashboardChatbotName') || 'Dashboard Chatbot Name'}
                fullWidth
                value={editFormData.dashboardChatbotName}
                onChange={(e) => setEditFormData({ ...editFormData, dashboardChatbotName: e.target.value })}
                placeholder="mag-hotel-internal"
                helperText={
                  t('superAdmin.dashboardChatbotNameHelp') ||
                  'Bu addaki Dashboard chatbot’un Gemini key + prompt + KB + MCP ayarları kullanılır. Boş = yerleşik PMS AI.'
                }
                sx={textFieldSx}
              />
              <TextField
                label={t('superAdmin.address')}
                fullWidth
                value={editFormData.address}
                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                sx={textFieldSx}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('superAdmin.city')}
                  fullWidth
                  value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.state')}
                  fullWidth
                  value={editFormData.state}
                  onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.zipCode')}
                  fullWidth
                  value={editFormData.zipCode}
                  onChange={(e) => setEditFormData({ ...editFormData, zipCode: e.target.value })}
                  sx={textFieldSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('superAdmin.country')}
                  fullWidth
                  value={editFormData.country}
                  onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.phone')}
                  fullWidth
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  sx={textFieldSx}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={t('superAdmin.email')}
                  type="email"
                  fullWidth
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  sx={textFieldSx}
                />
                <TextField
                  label={t('superAdmin.website')}
                  fullWidth
                  value={editFormData.website}
                  onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                  sx={textFieldSx}
                />
              </Stack>
              <TextField
                label={t('superAdmin.description')}
                fullWidth
                multiline
                rows={3}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                sx={textFieldSx}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.isActive}
                    onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                  />
                }
                label={t('superAdmin.active')}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={() => setEditDialog(false)}
            >
              {t('login.cancel')}
            </Button>
            <Button
              onClick={handleUpdateHotel}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#667eea',
                '&:hover': {
                  backgroundColor: '#5568d3',
                },
              }}
            >
              {t('superAdmin.saveChanges') || 'Kaydet'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Admin Credentials Dialog */}
        <Dialog 
          open={credentialsDialog} 
          onClose={() => setCredentialsDialog(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 700,
            color: isDark ? theme.palette.primary.light : '#667eea',
          }}>
            {t('superAdmin.hotelCreatedSuccess')}
          </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{t('superAdmin.important')}:</strong> {t('superAdmin.saveCredentials')}
          </Alert>
          <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.primary }}>
            {t('superAdmin.adminAccountCreated')}
          </Typography>
          <Paper sx={{ 
            p: 2, 
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('superAdmin.email')}
            </Typography>
            <Typography variant="body1" sx={{ 
              fontWeight: 600, 
              mb: 2, 
              fontFamily: 'monospace',
              color: theme.palette.text.primary,
            }}>
              {adminCredentials?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('superAdmin.password')}
            </Typography>
            <Typography variant="body1" sx={{ 
              fontWeight: 600, 
              fontFamily: 'monospace', 
              color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
            }}>
              {adminCredentials?.password}
            </Typography>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('superAdmin.credentialsInfo')}
          </Typography>
        </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => {
                if (adminCredentials) {
                  navigator.clipboard.writeText(
                    `Email: ${adminCredentials.email}\nPassword: ${adminCredentials.password}`
                  );
                }
              }}
            >
              {t('superAdmin.copyToClipboard')}
            </Button>
            <Button 
              onClick={() => setCredentialsDialog(false)} 
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#667eea',
                '&:hover': {
                  backgroundColor: '#5568d3',
                },
              }}
            >
              {t('superAdmin.savedCredentials')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogState.open}
          onClose={() => handleConfirmDialogClose(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 2,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{confirmDialogState.title || 'Onay'}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: '#374151' }}>
              {confirmDialogState.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
            <Button
              onClick={() => handleConfirmDialogClose(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 3,
                px: 3,
              }}
            >
              {confirmDialogState.cancelLabel || 'İptal'}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleConfirmDialogClose(true)}
              color="error"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 3,
                px: 3,
              }}
            >
              {confirmDialogState.confirmLabel || 'Onayla'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Info Dialog */}
        <Dialog
          open={infoDialog.open}
          onClose={closeInfoDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              textAlign: 'center',
              p: 2,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {infoDialog.title || 'Bilgilendirme'}
          </DialogTitle>
          <DialogContent>
            <Alert severity={infoDialog.severity} sx={{ borderRadius: 3, fontSize: '0.95rem' }}>
              <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                {infoDialog.message}
              </Box>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              variant="contained"
              onClick={closeInfoDialog}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 3,
                px: 4,
              }}
            >
              Tamam
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default SuperAdminPage;

